import React, { useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Tabs,
  Tab,
  Button,
  Link,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TaxIcon from '@mui/icons-material/Receipt';
import ConstructionIcon from '@mui/icons-material/Construction';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';

const FAQ = () => {
  const [tabValue, setTabValue] = useState(0);
  const navigate = useNavigate();

  const handleChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleContactClick = () => {
    navigate('/contact');
  };

  return (
    <Box sx={{ bgcolor: '#fbfbfb', minHeight: '100vh', py: 8 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: 700,
              color: '#0a233b',
              mb: 2,
              fontSize: { xs: '2rem', md: '3rem' }
            }}
          >
            Frequently Asked Questions
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#6a6565', 
              maxWidth: '800px', 
              mx: 'auto',
              fontSize: { xs: '1rem', md: '1.25rem' } 
            }}
          >
            Find answers to common questions about our services. If you need further assistance, please don't hesitate to contact us.
          </Typography>
        </Box>

        {/* Tabs */}
        <Paper elevation={3} sx={{ mb: 6, borderRadius: 2, overflow: 'hidden' }}>
          <Tabs
            value={tabValue}
            onChange={handleChange}
            variant="fullWidth"
            sx={{
              '.MuiTab-root': {
                fontWeight: 600,
                py: 2,
                fontSize: { xs: '0.8rem', md: '1rem' }
              },
              '.Mui-selected': {
                color: '#f66b10',
              },
              '.MuiTabs-indicator': {
                backgroundColor: '#f66b10',
              }
            }}
          >
            <Tab 
              icon={<TaxIcon />} 
              iconPosition="start" 
              label="MRI Tax Support" 
              sx={{ bgcolor: tabValue === 0 ? 'rgba(166, 209, 234, 0.1)' : 'transparent' }} 
            />
            <Tab 
              icon={<ConstructionIcon />} 
              iconPosition="start" 
              label="Project Management" 
              sx={{ bgcolor: tabValue === 1 ? 'rgba(166, 209, 234, 0.1)' : 'transparent' }} 
            />
            <Tab 
              icon={<HomeWorkIcon />} 
              iconPosition="start" 
              label="Property Management" 
              sx={{ bgcolor: tabValue === 2 ? 'rgba(166, 209, 234, 0.1)' : 'transparent' }} 
            />
          </Tabs>
        </Paper>

        {/* Tab Panels */}
        <Box sx={{ display: tabValue === 0 ? 'block' : 'none' }}>
          <MRITaxFAQ />
        </Box>
        <Box sx={{ display: tabValue === 1 ? 'block' : 'none' }}>
          <ProjectManagementFAQ />
        </Box>
        <Box sx={{ display: tabValue === 2 ? 'block' : 'none' }}>
          <PropertyManagementFAQ />
        </Box>

        {/* Contact CTA */}
        <Paper
          elevation={3}
          sx={{
            p: 4,
            mt: 6,
            borderRadius: 2,
            textAlign: 'center',
            backgroundImage: 'linear-gradient(to right, #0a233b, #1a3b5c)',
            color: '#fff',
          }}
        >
          <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600, color: '#a6d1ea' }}>
            Still Have Questions?
          </Typography>
          <Typography variant="body1" paragraph sx={{ maxWidth: '600px', mx: 'auto', mb: 3 }}>
            Can't find what you're looking for? Our team is here to help. Contact us for personalized assistance.
          </Typography>
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            onClick={handleContactClick}
            sx={{
              bgcolor: '#f66b10',
              '&:hover': {
                bgcolor: '#e05a00',
              },
              px: 4,
              py: 1.5,
              fontSize: '1rem',
            }}
          >
            Contact Us Now
          </Button>
        </Paper>

        {/* Footer info */}
        <Box sx={{ mt: 6, textAlign: 'center', color: '#6a6565' }}>
          <Typography variant="body2">
            📞 Call/WhatsApp: 0736261808 / 0722245676
          </Typography>
          <Typography variant="body2">
            📍 Office Location: Ouru Complex, 3rd Floor (Above Equity), Kisii
          </Typography>
          <Link 
            href="https://www.lantenapm.com" 
            sx={{ 
              color: '#f66b10', 
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            <Typography variant="body2">
              🌐 www.lantenapm.com
            </Typography>
          </Link>
        </Box>
      </Container>
    </Box>
  );
};

// TAB 1: MRI Tax Support
const MRITaxFAQ = () => {
  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2" sx={{ color: '#0a233b', mb: 2, fontWeight: 600 }}>
          MRI Tax Support
        </Typography>
        <Typography variant="body1" sx={{ color: '#6a6565', mb: 3 }}>
          At Lantena Property Managers, we understand that Monthly Rental Income (MRI) Tax Compliance can be confusing and time-consuming for landlords and property owners. To help you navigate this crucial aspect of real estate investment, we've compiled frequently asked questions about MRI tax compliance and how we simplify the process for you.
        </Typography>
      </Box>
      
      {/* FAQs */}
      <Box sx={{ mb: 5 }}>
        <FAQItem 
          question="What is Monthly Rental Income (MRI) Tax?" 
          answer={<Typography variant="body1">
            MRI tax is a mandatory tax on rental income imposed by the Kenya Revenue Authority (KRA). It applies to landlords earning between KES 288,000 and KES 15 million per year from rental properties.
          </Typography>}
        />
        
        <FAQItem 
          question="Who is Required to Pay MRI Tax?" 
          answer={<>
            <Typography variant="body1" paragraph>
              Any individual or business that earns rental income from property in Kenya is required to pay MRI tax. This includes:
            </Typography>
            <ul style={{ paddingLeft: '20px', marginTop: 0 }}>
              <li>✔ Private landlords</li>
              <li>✔ Real estate investors</li>
              <li>✔ Companies owning rental properties</li>
              <li>✔ Kenyans in the diaspora with rental properties in Kenya</li>
            </ul>
          </>}
        />
        
        <FAQItem 
          question="How is MRI Tax Calculated?" 
          answer={<>
            <Typography variant="body1" paragraph>
              MRI tax is charged at 10% of gross rental income. This means that:
            </Typography>
            <ul style={{ paddingLeft: '20px', marginTop: 0 }}>
              <li>If your rental income is KES 100,000 per month, your tax is KES 10,000.</li>
              <li>If your rental income is KES 500,000 per month, your tax is KES 50,000.</li>
            </ul>
            <Typography variant="body1" paragraph>
              No deductions (such as repairs, loans, or management fees) are allowed under MRI tax.
            </Typography>
          </>}
        />
        
        <FAQItem 
          question="When is MRI Tax Due?" 
          answer={<Typography variant="body1">
            MRI tax must be filed and paid by the 20th of every month. Late payment attracts penalties and interest charges from KRA.
          </Typography>}
        />
        
        <FAQItem 
          question="What Happens If I Don't Pay MRI Tax?" 
          answer={<>
            <Typography variant="body1" paragraph>
              Failure to comply with MRI tax regulations can result in:
            </Typography>
            <ul style={{ paddingLeft: '20px', marginTop: 0 }}>
              <li>🚨 Penalties – 5% of the unpaid tax</li>
              <li>🚨 Interest Charges – 1% per month on overdue amounts</li>
              <li>🚨 Legal Consequences – KRA audits and potential legal action</li>
            </ul>
            <Typography variant="body1" paragraph>
              Lantena helps avoid penalties by ensuring timely filing and compliance.
            </Typography>
          </>}
        />
        
        <FAQItem 
          question="Can I Claim Expenses Before Paying MRI Tax?" 
          answer={<>
            <Typography variant="body1" paragraph>
              No. MRI tax is based on gross rental income, meaning you cannot deduct expenses like maintenance, mortgage payments, or renovations before tax calculation.
            </Typography>
            <Typography variant="body1" paragraph>
              However, if you own multiple properties or a real estate business, you may qualify for alternative tax structures. Lantena advises on the best tax-saving strategies based on your situation.
            </Typography>
          </>}
        />
        
        <FAQItem 
          question="How Do I File My MRI Tax Returns?" 
          answer={<>
            <Typography variant="body1" paragraph>
              MRI tax returns must be filed through the KRA iTax portal every month. The process involves:
            </Typography>
            <ul style={{ paddingLeft: '20px', marginTop: 0 }}>
              <li>✔ Logging into KRA iTax</li>
              <li>✔ Declaring rental income earned in the month</li>
              <li>✔ Calculating the 10% tax amount</li>
              <li>✔ Making payment through KRA's approved payment methods</li>
            </ul>
            <Typography variant="body1" paragraph>
              At Lantena, we handle all tax filings for you, ensuring accuracy and compliance.
            </Typography>
          </>}
        />
        
        <FAQItem 
          question="Do I Still Pay MRI Tax If My Tenants Didn't Pay Rent?" 
          answer={<>
            <Typography variant="body1" paragraph>
              Yes! MRI tax is based on total rent receivable, not actual rent collected. This means you must pay tax even if tenants default.
            </Typography>
            <Typography variant="body1" paragraph>
              To avoid such risks, Lantena provides strict tenant screening and rent collection solutions.
            </Typography>
          </>}
        />
      </Box>
    </Box>
  );
};

// TAB 2: Project Management
const ProjectManagementFAQ = () => {
  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2" sx={{ color: '#0a233b', mb: 2, fontWeight: 600 }}>
          Project Management & Luxury Renovations
        </Typography>
        <Typography variant="body1" sx={{ color: '#6a6565', mb: 3 }}>
          Investing in real estate development or luxury renovations can be rewarding, but it comes with challenges. At Lantena Property Managers, we provide expert project management services to ensure your property is built or renovated efficiently, on time, and within budget.
        </Typography>
      </Box>
      
      {/* FAQs */}
      <Box sx={{ mb: 5 }}>
        <FAQItem 
          question="What is Project Management in Real Estate, and Why Do I Need It?" 
          answer={<>
            <Typography variant="body1" paragraph>
              Project management ensures that every stage of construction or renovation—from design to completion—is executed professionally, on schedule, and within budget. Without proper management, investors often experience cost overruns, poor workmanship, and project delays.
            </Typography>
            <Typography variant="body1" paragraph>
              At Lantena Property Managers, we act as your project overseer, handling everything from contractor selection to quality control and financial tracking, so you get the best results with minimal stress.
            </Typography>
          </>}
        />
        
        <FAQItem 
          question="How Does Lantena Handle Luxury Renovations?" 
          answer={<>
            <Typography variant="body1" paragraph>
              Luxury renovations go beyond standard remodeling; they involve high-end materials, custom designs, and smart home integrations. We ensure that your renovation enhances the property's value, functionality, and aesthetics by:
            </Typography>
            <ul style={{ paddingLeft: '20px', marginTop: 0 }}>
              <li>✔ Conducting feasibility studies to determine the best luxury enhancements</li>
              <li>✔ Sourcing top-tier materials & fittings for high-end results</li>
              <li>✔ Hiring expert designers & contractors with a proven track record</li>
              <li>✔ Ensuring smart home automation for energy efficiency & convenience</li>
            </ul>
          </>}
        />
        
        <FAQItem 
          question="I Live Abroad. How Can I Monitor My Project in Kenya?" 
          answer={<>
            <Typography variant="body1" paragraph>
              We use project management software that allows you to:
            </Typography>
            <ul style={{ paddingLeft: '20px', marginTop: 0 }}>
              <li>📲 Track progress in real-time via live updates and photos</li>
              <li>📑 Receive budget reports & financial summaries on request</li>
              <li>🎥 Conduct virtual site visits for inspections and approvals</li>
            </ul>
            <Typography variant="body1" paragraph>
              This means you can manage your investment from anywhere in the world with full transparency.
            </Typography>
          </>}
        />
        
        <FAQItem 
          question="How Do You Ensure My Project Stays on Budget?" 
          answer={<>
            <Typography variant="body1" paragraph>
              We eliminate budget overruns through:
            </Typography>
            <ul style={{ paddingLeft: '20px', marginTop: 0 }}>
              <li>✔ Detailed cost estimation before the project begins</li>
              <li>✔ Negotiation with contractors to get the best pricing</li>
              <li>✔ Tracking expenses with financial reports</li>
              <li>✔ Regular budget reviews to prevent unnecessary costs</li>
            </ul>
            <Typography variant="body1" paragraph>
              With Lantena, you stay informed and in control of your project's finances.
            </Typography>
          </>}
        />
        
        <FAQItem 
          question="How Long Does a Construction or Renovation Project Take?" 
          answer={<>
            <Typography variant="body1" paragraph>
              Project timelines depend on the scope of work:
            </Typography>
            <ul style={{ paddingLeft: '20px', marginTop: 0 }}>
              <li>🏗 New Construction (Apartments, Homes, Villas) → 6-18 months</li>
              <li>🏠 Full Luxury Renovations → 3-12 months</li>
              <li>🎨 Minor Renovations (Kitchens, Bathrooms, Interiors) → 4-16 weeks</li>
            </ul>
            <Typography variant="body1" paragraph>
              We provide realistic timelines and frequent progress reports to ensure completion as scheduled.
            </Typography>
          </>}
        />
      </Box>
    </Box>
  );
};

// TAB 3: Property Management
const PropertyManagementFAQ = () => {
  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2" sx={{ color: '#0a233b', mb: 2, fontWeight: 600 }}>
          Property Management
        </Typography>
        <Typography variant="body1" sx={{ color: '#6a6565', mb: 3 }}>
          Managing rental properties can be rewarding, but it also comes with challenges. Many property owners—especially those living abroad or with busy schedules—struggle with rent collection, maintenance, tenant screening, and compliance with tax regulations. This is where a professional property management company like Lantena Property Managers comes in.
        </Typography>
      </Box>
      
      {/* FAQs */}
      <Box sx={{ mb: 5 }}>
        <FAQItem 
          question="What does a property management company do?" 
          answer={<>
            <Typography variant="body1" paragraph>
              A property management company takes care of the day-to-day operations of rental properties on behalf of landlords. This includes:
            </Typography>
            <ul style={{ paddingLeft: '20px', marginTop: 0 }}>
              <li>✅ Rent collection and financial management</li>
              <li>✅ Tenant screening and lease enforcement</li>
              <li>✅ Maintenance and repairs</li>
              <li>✅ Handling tenant inquiries and complaints</li>
              <li>✅ Legal compliance and eviction processes</li>
              <li>✅ Property marketing and tenant placement</li>
            </ul>
            <Typography variant="body1" paragraph>
              Lantena Property Managers ensures that landlords enjoy stress-free property ownership while maximizing returns on their investments.
            </Typography>
          </>}
        />
        
        <FAQItem 
          question="Why should I hire a property management company instead of managing my property myself?" 
          answer={<>
            <Typography variant="body1" paragraph>
              Managing a property yourself can be time-consuming and stressful. A professional management company:
            </Typography>
            <ul style={{ paddingLeft: '20px', marginTop: 0 }}>
              <li>🔹 Saves you time by handling tenant issues and maintenance requests.</li>
              <li>🔹 Ensures timely rent collection and reduces late payments.</li>
              <li>🔹 Protects your investment by enforcing lease agreements.</li>
              <li>🔹 Minimizes vacancies with professional marketing and tenant placement.</li>
              <li>🔹 Keeps you compliant with property tax laws and regulations.</li>
            </ul>
            <Typography variant="body1" paragraph>
              For landlords living abroad or with multiple properties, hiring a property manager is a smart financial decision that ensures peace of mind.
            </Typography>
          </>}
        />
        
        <FAQItem 
          question="How much does property management cost?" 
          answer={<Typography variant="body1">
            Property management fees typically range from 7% to 10% of the monthly rental income, depending on the level of service provided. At Lantena, we offer flexible packages to suit different property types and landlord needs. For landlords requiring additional services such as tax support, security, and maintenance management, we provide customized packages at competitive industry rates.
          </Typography>}
        />
        
        <FAQItem 
          question="How does rent collection work?" 
          answer={<>
            <Typography variant="body1" paragraph>
              Lantena simplifies rent collection through our advanced property management software, ensuring that rent is deposited directly into the landlord's account. Our system:
            </Typography>
            <ul style={{ paddingLeft: '20px', marginTop: 0 }}>
              <li>✅ Sends automatic rent reminders to tenants.</li>
              <li>✅ Tracks payments and generates real-time financial reports.</li>
              <li>✅ Reduces the risk of late or missed payments.</li>
            </ul>
            <Typography variant="body1" paragraph>
              With our tech-driven approach, landlords never have to chase tenants for rent again.
            </Typography>
          </>}
        />
        
        <FAQItem 
          question="What happens if a tenant doesn't pay rent?" 
          answer={<>
            <Typography variant="body1" paragraph>
              Late or missed rent payments can be a challenge. Our approach includes:
            </Typography>
            <ul style={{ paddingLeft: '20px', marginTop: 0 }}>
              <li>✔ Sending automatic reminders to tenants.</li>
              <li>✔ Direct follow-up through calls or emails.</li>
              <li>✔ Issuing formal legal notices for non-payment.</li>
              <li>✔ Handling eviction proceedings if necessary (as per Kenyan landlord-tenant laws).</li>
            </ul>
            <Typography variant="body1" paragraph>
              We ensure that landlords always receive their rental income while following the legal framework.
            </Typography>
          </>}
        />
      </Box>
    </Box>
  );
};

// FAQItem component
const FAQItem = ({ question, answer }) => {
  return (
    <Accordion 
      sx={{ 
        mb: 2, 
        borderRadius: '8px !important', 
        overflow: 'hidden',
        border: '1px solid #eaeaea',
        '&:before': {
          display: 'none',
        },
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: '#0a233b' }} />}
        sx={{ 
          bgcolor: 'rgba(166, 209, 234, 0.1)',
          '&.Mui-expanded': {
            borderBottom: '1px solid #eaeaea',
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <HelpOutlineIcon sx={{ color: '#f66b10', mr: 1 }} />
          <Typography variant="h6" component="h3" sx={{ fontWeight: 600, color: '#0a233b', fontSize: { xs: '1rem', md: '1.1rem' } }}>
            {question}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 3, bgcolor: '#fff' }}>
        {answer}
      </AccordionDetails>
    </Accordion>
  );
};

export default FAQ;
